import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardGrid } from './CardGrid';
import IntegratedSearchBar from './IntegratedSearchBar';
import DealGridSeo from './DealGridSeo';
import DealCountHeader from './DealCountHeader';
import LoadMoreFooter from './LoadMoreFooter';
import NoDealsState from './NoDealsState';
import { NoSearchResults, NoFilterResults } from './EmptyResults';
import useDeals from '../../hooks/useDeals';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import HeroBannerCarousel from '../common/HeroBannerCarousel';
import PageIntro from '../common/PageIntro';
import CategoryLinks from './CategoryLinks';
import PrimeDayBanner from './PrimeDayBanner';
import useCategoryPages from '../../hooks/useCategoryPages';
import { categoryIntro, categoryPath } from '../../utils/categorySlug';
import { trackSearch } from '../../utils/analytics';
import './DealGrid.css';

/**
 * Homepage, or a category landing page when `landing` is set (CategoryLanding.js):
 * hero/intro, search/filter bar, deal count, the grid, Load More and category links.
 * All list state lives in the deals slice (via useDeals); this component only
 * decides which of the states below to render:
 *
 *   error → loading → results | no search results | no filter results | empty feed
 */
const DealGrid = ({ landing = null }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?search= and ?category= let other pages (e.g. the product page) open a filtered grid
  const urlSearchQuery = searchParams.get('search') || '';
  const urlCategory = searchParams.get('category') || '';
  const storedCategory = useSelector((state) => state.deals.currentCategory);
  const { categoryPages } = useCategoryPages();

  // A category page always shows its category. The homepage shows everything, so a
  // category left in the store by a category page is reset on the way back.
  let initialCategory = urlCategory;
  if (landing) initialCategory = landing.name;
  else if (!initialCategory && storedCategory && storedCategory.toLowerCase() !== 'all') initialCategory = 'all';

  const {
    deals,
    loading,
    loadingMore,
    error,
    loadMoreError,
    currentSearchTerm,
    currentCategory,
    currentSort,
    activeFilters,
    pagination,
    search,
    filterByCategory,
    filterByTags,
    sortBy,
    loadMore,
    clearError,
    clearLoadMoreError,
    clearSearch,
    clearFilters,
  } = useDeals({ searchQuery: urlSearchQuery, category: initialCategory });
  const { modal } = useSelector((state) => state.deals);
  const lastTrackedSearch = useRef('');

  // Clean up URL params after they've been consumed (mount only)
  useEffect(() => {
    if (urlSearchQuery || urlCategory) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track search results when search completes
  useEffect(() => {
    if (!loading && currentSearchTerm && currentSearchTerm !== lastTrackedSearch.current) {
      trackSearch(currentSearchTerm, pagination.totalDeals || deals.length, 'keyword');
      lastTrackedSearch.current = currentSearchTerm;
    }
  }, [loading, currentSearchTerm, pagination.totalDeals, deals.length]);

  // Which empty state applies when nothing comes back
  const hasCategory = Boolean(currentCategory && currentCategory.toLowerCase() !== 'all');
  const isFiltered = Boolean(currentSearchTerm || hasCategory || activeFilters.length > 0);
  const isEmpty = !loading && deals.length === 0;

  // On a category page, "Clear filters" keeps the category (it is the page itself);
  // if only the category is left and it's empty, go back to all deals.
  const clearAll = () => {
    if (!landing) clearFilters();
    else if (activeFilters.length > 0) filterByTags([]);
    else navigate('/');
  };

  // Picking a category opens its landing page (a real, shareable URL) when it has one
  const changeCategory = (category) => {
    if (!category || category.toLowerCase() === 'all') {
      if (landing) navigate('/');
      else filterByCategory('all');
      return;
    }
    const path = categoryPath(category, categoryPages);
    if (path && (!landing || path !== `/category/${landing.slug}`)) navigate(path);
    else filterByCategory(category);
  };

  const searchBar = (
    <IntegratedSearchBar
      onSearch={(query) => search(query)}
      onCategoryChange={changeCategory}
      selectedCategory={currentCategory}
      loading={loading}
      currentSort={currentSort}
      onSortChange={sortBy}
      activeFilters={activeFilters}
      onFilterChange={filterByTags}
    />
  );

  if (error) {
    return (
      <div className="container mt-4">
        {searchBar}
        <ErrorAlert
          error={error}
          title="We couldn’t load deals"
          // Repeat whatever failed: the current search, or the default feed
          onRetry={() => search(currentSearchTerm || '')}
          onDismiss={clearError}
        />
      </div>
    );
  }

  return (
    <div>
      <DealGridSeo searchTerm={currentSearchTerm} deals={deals} landing={landing} />

      <header className="deals-header">
        <div className="container">
          {currentSearchTerm && (
            <h1 className="sr-only">{currentSearchTerm} Deals and Discounts - Lite Pocket Deals</h1>
          )}
          {!currentSearchTerm && landing && <PageIntro {...categoryIntro(landing)} />}
          {!currentSearchTerm && !landing && <PrimeDayBanner />}
          {!currentSearchTerm && !landing && <HeroBannerCarousel />}
        </div>
      </header>

      {searchBar}

      {!loading && deals.length > 0 && pagination.totalDeals > 0 && (
        <DealCountHeader
          pagination={pagination}
          searchTerm={currentSearchTerm}
          category={currentCategory}
        />
      )}

      {loading && <LoadingSpinner />}

      {isEmpty && currentSearchTerm && <NoSearchResults searchTerm={currentSearchTerm} onShowAll={clearSearch} />}
      {isEmpty && !currentSearchTerm && isFiltered && <NoFilterResults onClearFilters={clearAll} />}
      {isEmpty && !currentSearchTerm && !isFiltered && <NoDealsState />}

      {!loading && deals.length > 0 && (
        <>
          <CardGrid list={deals} modal={modal} />
          <LoadMoreFooter
            pagination={pagination}
            loadingMore={loadingMore}
            loadMoreError={loadMoreError}
            onLoadMore={loadMore}
            onDismissError={clearLoadMoreError}
          />
        </>
      )}

      {!loading && <CategoryLinks currentSlug={landing ? landing.slug : undefined} />}
    </div>
  );
};

DealGrid.propTypes = {
  landing: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    maxPercentOff: PropTypes.number,
  }),
};

export default DealGrid;
